import posthog from "posthog-js";

import { analyticsContext, isAnalyticsEnabled } from "./posthog";
import { eventError, eventId, type EventOwner, ownerForAddress } from "./walletEvents";
import { walletJourney } from "./walletJourney";

export type ProductFlow =
    | "rewards_distribution"
    | "rewards_destination"
    | "rewards_routing"
    | "saved_destination"
    | "swap"
    | "stats"
    | "history"
    | "earn_compare"
    | "navigation"
    | "claim"
    | "rewards";
export type ProductAction =
    | "requested"
    | "submit_requested"
    | "form_viewed"
    | "form_closed"
    | "viewed"
    | "closed"
    | "state_viewed"
    | "selected"
    | "changed"
    | "validation_updated"
    | "retry_clicked"
    | "link_clicked"
    | "started"
    | "challenge_started"
    | "challenge_completed"
    | "challenge_failed"
    | "verification_method_selected"
    | "verification_consent_viewed"
    | "verification_consent_accepted"
    | "verification_consent_rejected"
    | "verification_started"
    | "proof_received"
    | "verification_failed"
    | "verification_resumed"
    | "transfer_started"
    | "transfer_submitted"
    | "transfer_failed"
    | "transfer_confirmation_deferred"
    | "submitted"
    | "completed"
    | "failed"
    | "confirmation_deferred"
    | "result_viewed"
    | "copy_requested"
    | "copied"
    | "copy_failed"
    | "tab_selected"
    | "range_selected"
    | "page_selected"
    | "section_toggled"
    | "detail_viewed"
    | "menu_opened"
    | "menu_closed"
    | "token_picker_opened"
    | "token_picker_closed"
    | "token_selected"
    | "amount_updated"
    | "direction_changed"
    | "quote_received"
    | "quote_failed"
    | "stage_changed"
    | "canceled"
    | "blocked"
    | "receipt_toggled";
export type ProductEvent = `${ProductFlow}:${ProductAction}`;
export type ProductEntry =
    | "rewards_footer"
    | "position_row"
    | "routing_panel"
    | "destination_book"
    | "rewards_entry"
    | "landing_buy_widget"
    | "rewards_buy_panel"
    | "swap_widget"
    | "header"
    | "mobile_menu"
    | "footer"
    | "stats_page"
    | "stats_distributions"
    | "rewards_history"
    | "earn_comparison"
    | "rewards_summary";

/** Explicit fields only. Never send addresses, labels, signatures, search text, or errors. */
export interface ProductProperties {
    entry_point?: ProductEntry;
    flow_id?: string;
    attempt_id?: string;
    attempt_number?: number;
    duration_ms?: number;
    failure_stage?: string;
    error_code?: string;
    error_category?: string;
    http_status?: number;
    close_method?: string;
    request_pending?: boolean;
    distribution_mode?: "stream" | "claim";
    previous_mode?: "stream" | "claim";
    destination_kind?: "self" | "custom";
    has_label?: boolean;
    address_valid?: boolean;
    can_submit?: boolean;
    reason?: string;
    method?: string;
    verification_method?: string;
    proof_source?: string;
    fallback_triggered?: boolean;
    transfer_available?: boolean;
    result?: string;
    status?: string;
    control?: string;
    mode?: string;
    tab?: string;
    previous_tab?: string;
    range?: string;
    section?: string;
    expanded?: boolean;
    page?: number;
    direction?: "next" | "previous" | "more";
    item_count?: number;
    destination_count?: number;
    allocation_total_bps?: number;
    action?: string;
    link_type?: string;
    target?: string;
    source_token?: string;
    target_token?: string;
    amount_bucket?: "zero" | "under_1" | "1_to_10" | "10_to_100" | "100_to_1000" | "1000_plus";
    side?: "send" | "receive";
    stage?: string;
    is_available?: boolean;
    is_empty?: boolean;
    advanced?: boolean;
}
export type ProductEmit = (event: ProductEvent, properties: ProductProperties, owner: EventOwner) => void;

export const captureProductEvent: ProductEmit = (event, properties, owner): void => {
    if (!isAnalyticsEnabled()) return;
    const payload = {
        ...analyticsContext(),
        ...owner.properties,
        ...properties,
        flow: event.split(":")[0],
        schema_version: 3,
        route: window.location.pathname
    };
    const send = (identity?: string): void => {
        try {
            posthog.capture(event, { ...payload, ...(identity ? { distinct_id: identity } : {}) });
        } catch {
            /* Analytics cannot block the app. */
        }
    };
    if (owner.identity)
        void owner.identity
            .then((identity) => {
                if (identity) send(identity);
            })
            .catch(() => undefined);
    else send();
};

export function productEvent(event: ProductEvent, properties: ProductProperties): void {
    captureProductEvent(event, properties, walletJourney.owner());
}

/** A panel owns a flow; each explicit submit owns an attempt. Late results retain that owner. */
export class ProductJourney {
    readonly id = eventId();
    readonly owner: EventOwner;
    private readonly observations = new Map<ProductAction, string>();
    private count = 0;
    private closed = false;
    constructor(
        readonly flow: ProductFlow,
        readonly entry: ProductEntry,
        private readonly address?: string,
        private readonly emit: ProductEmit = captureProductEvent
    ) {
        this.owner = address ? ownerForAddress(address, walletJourney.owner().properties) : walletJourney.owner();
    }
    currentOwner(): EventOwner {
        return this.address ? this.owner : walletJourney.owner();
    }
    send(action: ProductAction, properties: ProductProperties = {}, owner = this.currentOwner()): void {
        try {
            this.emit(`${this.flow}:${action}`, { ...properties, entry_point: this.entry, flow_id: this.id }, owner);
        } catch {
            /* Telemetry must not interrupt the action. */
        }
    }
    changed(action: ProductAction, properties: ProductProperties = {}): void {
        const key = JSON.stringify(properties);
        if (this.observations.get(action) === key || (action === "form_viewed" && this.observations.has(action)))
            return;
        this.observations.set(action, key);
        this.send(action, properties);
    }
    close(method: string, properties: ProductProperties = {}): void {
        if (this.closed) return;
        this.closed = true;
        this.send("form_closed", { ...properties, close_method: method });
    }
    attempt(properties: ProductProperties = {}): ProductAttempt {
        return new ProductAttempt(this, ++this.count, properties);
    }
}

export class ProductAttempt {
    readonly id = eventId();
    private readonly startedAt = performance.now();
    private terminal = false;
    private submitted = false;
    private transferPending = false;
    private readonly owner: EventOwner;
    private phase = "challenge";
    constructor(
        private readonly journey: ProductJourney,
        private readonly number: number,
        private readonly properties: ProductProperties
    ) {
        this.owner = journey.currentOwner();
        this.send("started");
    }
    send(action: ProductAction, properties: ProductProperties = {}): void {
        if (action === "transfer_submitted" || action === "transfer_confirmation_deferred") this.transferPending = true;
        if (action === "proof_received" || action === "transfer_failed") this.transferPending = false;
        this.journey.send(
            action,
            {
                ...this.properties,
                ...properties,
                attempt_id: this.id,
                attempt_number: this.number
            },
            this.owner
        );
    }
    stage(stage: string): void {
        this.phase = stage;
    }
    challengeCompleted(): void {
        this.send("challenge_completed");
        this.phase = "verification";
    }
    submit(method: string): void {
        this.phase = "submit";
        this.submitted = true;
        this.send("submitted", { verification_method: method === "microTransfer" ? "micro_transfer" : "sign_message" });
    }
    complete(properties: ProductProperties = {}): void {
        if (this.terminal) return;
        this.terminal = true;
        this.send("completed", { ...properties, duration_ms: performance.now() - this.startedAt });
    }
    fail(error: unknown): void {
        if (this.terminal) return;
        this.terminal = true;
        const normalized = eventError(error);
        // A failed response after dispatch can be an unknown write outcome.
        const uncertain =
            this.transferPending ||
            (this.submitted && (normalized.error_category === "network" || normalized.error_category === "timeout"));
        if (this.phase === "challenge") this.send("challenge_failed", normalized);
        this.send(uncertain ? "confirmation_deferred" : "failed", {
            ...normalized,
            failure_stage: this.phase,
            duration_ms: performance.now() - this.startedAt
        });
    }
    endFailure(error: unknown, uncertain = false): void {
        if (this.terminal) return;
        this.terminal = true;
        this.send(uncertain ? "confirmation_deferred" : "failed", {
            ...eventError(error),
            failure_stage: this.phase,
            duration_ms: performance.now() - this.startedAt
        });
    }
}
